import React, { useState, useEffect } from "react";
import { LogOut, Globe, Menu, X } from "lucide-react";
import { useT } from "../i18n";
import { NAV } from "../constants/testIds";
import RotatingLogo from "./RotatingLogo";

const TABS = [
    { key: "landing", labelKey: "navLanding", testId: NAV.tabLanding },
    { key: "simulator", labelKey: "navSimulator", testId: NAV.tabSimulator },
    { key: "peda", labelKey: "navPeda", testId: NAV.tabPeda },
    { key: "crm", labelKey: "navCrm", testId: NAV.tabCrm, admin: true },
    { key: "settings", labelKey: "navSettings", testId: NAV.tabSettings, admin: true },
    { key: "integrations", labelKey: "navIntegrations", testId: NAV.tabIntegrations, admin: true },
];

export default function Header({ lang, setLang, activeTab, setActiveTab, isAdmin, onLogout }) {
    const T = useT(lang);
    const [mobileOpen, setMobileOpen] = useState(false);

    // Close the mobile menu when a tab is picked
    const pickTab = (key) => {
        setActiveTab(key);
        setMobileOpen(false);
    };

    // Close mobile menu when resizing to desktop
    useEffect(() => {
        const handler = () => { if (window.innerWidth >= 768) setMobileOpen(false); };
        window.addEventListener("resize", handler);
        return () => window.removeEventListener("resize", handler);
    }, []);

    return (
        <header className="backdrop-blur-xl bg-white/95 border-b border-[#F1E4D2] sticky top-0 z-50">
            <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-2 md:py-3 flex items-center gap-3">
                {/* Brand */}
                <button
                    onClick={() => pickTab("landing")}
                    className="flex items-center gap-3 cursor-pointer shrink-0"
                    data-testid="brand-logo"
                    aria-label="home"
                >
                    <RotatingLogo size="sm" lang={lang} showName={false} />
                </button>

                {/* Desktop nav */}
                <nav className="hidden md:flex gap-1 flex-wrap mx-auto md:mx-0">
                    {TABS.map((tab) => (
                        <button
                            key={tab.key}
                            data-testid={tab.testId}
                            onClick={() => pickTab(tab.key)}
                            className={`tab-pill ${activeTab === tab.key ? "active" : ""}`}
                        >
                            {T[tab.labelKey]}
                            {tab.admin && (
                                <span className="text-[10px] mr-1 opacity-70">🔒</span>
                            )}
                        </button>
                    ))}
                </nav>

                {/* Right-side controls */}
                <div className="flex items-center gap-2 ms-auto">
                    <button
                        data-testid={NAV.langSwitcher}
                        onClick={() => setLang(lang === "he" ? "ar" : "he")}
                        className="btn-outline !py-1 !px-2 md:!py-1.5 md:!px-3 !text-xs md:!text-sm"
                    >
                        <Globe size={14} />
                        <span className="hidden sm:inline">{T.langSwitch}</span>
                    </button>
                    {isAdmin && (
                        <button
                            data-testid={NAV.adminLogout}
                            onClick={onLogout}
                            className="btn-outline !py-1 !px-2 md:!py-1.5 md:!px-3 !text-xs md:!text-sm"
                            title={T.logout}
                        >
                            <LogOut size={14} />
                            <span className="hidden md:inline">{T.logout}</span>
                        </button>
                    )}

                    {/* Mobile hamburger */}
                    <button
                        onClick={() => setMobileOpen((v) => !v)}
                        className="md:hidden w-9 h-9 rounded-lg border border-[#F1E4D2] bg-white text-[#1A365D] flex items-center justify-center hover:bg-[#FFF8F0]"
                        aria-label={mobileOpen ? "close menu" : "open menu"}
                        data-testid="nav-mobile-toggle"
                    >
                        {mobileOpen ? <X size={18} /> : <Menu size={18} />}
                    </button>
                </div>
            </div>

            {/* Mobile drawer */}
            {mobileOpen && (
                <div className="md:hidden border-t border-[#F1E4D2] bg-white/98 backdrop-blur-xl">
                    <nav className="max-w-7xl mx-auto px-3 py-2 flex flex-col gap-1" data-testid="nav-mobile-drawer">
                        {TABS.map((tab) => (
                            <button
                                key={tab.key}
                                data-testid={tab.testId + "-mobile"}
                                onClick={() => pickTab(tab.key)}
                                className={`w-full text-start px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                                    activeTab === tab.key
                                        ? "bg-[#1A365D] text-white"
                                        : "text-[#1A365D] hover:bg-[#FFF8F0]"
                                }`}
                            >
                                {T[tab.labelKey]}
                                {tab.admin && (
                                    <span className="text-[10px] ms-1 opacity-70">🔒</span>
                                )}
                            </button>
                        ))}
                    </nav>
                </div>
            )}
        </header>
    );
}
